import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { toLower } from 'lodash';
import { filter, take } from 'rxjs';
import { LOCALIZATION_CONFIG, LocalizationConfig } from './localization.model';
import { getLocaleConfig, registerSupportedLocaleData } from './locale-data';

@Injectable()
export class LocalizationService {
  private devMode = true;

  private defaultLocale = 'en-gb';

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Optional()
    @Inject(LOCALIZATION_CONFIG)
    readonly localizationConfig: LocalizationConfig
  ) {
    if (!localizationConfig) {
      return;
    }

    registerSupportedLocaleData();

    if ('devMode' in localizationConfig) {
      this.devMode = localizationConfig.devMode as boolean;
    }

    if ('defaultLocale' in localizationConfig) {
      this.defaultLocale = localizationConfig.defaultLocale as string;
    }
  }

  initialiseLocale(locale?: string) {
    if (locale) this.defaultLocale = locale;

    const storedLocale = this.getStorageLocale();

    if (storedLocale) {
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationStart),
          take(1)
        )
        .subscribe((e) => {
          this.setLocale(storedLocale, (e as NavigationStart).url);
        });
    }
  }

  getLocaleConfig() {
    const locale = this.getDocumentLocale();
    return getLocaleConfig(locale);
  }

  getDocumentLocale(): string {
    return this.document.documentElement.getAttribute('lang') || '';
  }

  setLocale(locale: string, route?: string) {
    const requestLocale = toLower(locale);
    const docLocale = toLower(this.getDocumentLocale());
    const storedLocale = toLower(this.getStorageLocale() || '');

    if (requestLocale !== docLocale) {
      this.switchLocale(locale, route);
    }
    if (requestLocale !== storedLocale) {
      this.setStorageLocale(locale);
    }
  }

  switchLocale(locale: string, route?: string) {
    if (!this.devMode) {
      let href = route ? `${route}` : `${this.router.url}`;

      if (toLower(locale) !== toLower(this.defaultLocale)) {
        href = route ? `/${locale}${route}` : `/${locale}${this.router.url}`;
      }

      window.location.href = href;
    }

    this.setStorageLocale(locale);
  }

  getLocalePreference() {
    return this.getStorageLocale() || this.defaultLocale;
  }

  private getStorageLocale() {
    return localStorage.getItem('locale');
  }

  private setStorageLocale(locale: string) {
    localStorage.setItem('locale', locale);
  }
}
